import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import GetCookie from '../../utils/GetCookie';
import DocumentPreview from '../../utils/DocumentPreview';
import DeleteConfirmationModal from '../../utils/DeleteConfirmationModal';
import PaginationControls from '../../utils/PaginationControls';
import { StatusBadge, StatusBadgeXSm } from '../../utils/StatusBadge';
import TrimContent from '../../utils/TrimContent';
import { useTimezone } from '../../utils/TimezoneContext';
import { PERMISSIONS, capabilityChecker } from '../../utils/permissionRoutes';

const GenbiReportView = ({ templateId, onError, onProcessingStart, onProcessingEnd, onRefreshTemplateList, registerRuntimeRefresh }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [totalReports, setTotalReports] = useState(0);
  const [genStatus, setGenStatus] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewS3Path, setPreviewS3Path] = useState(null);
  const [previewDocumentName, setPreviewDocumentName] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedReports, setSelectedReports] = useState([]);
  const [deleteMode, setDeleteMode] = useState('single');
  const [reportToDelete, setReportToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [capabilities, setCapabilities] = useState({});
  const canHere = capabilityChecker(capabilities);

  const API_BASE_URL = '/pceapi/genbi/';

  const handleProcessingEndWithRefresh = () => {
    if (onProcessingEnd) {
      onProcessingEnd();
    }
    if (onRefreshTemplateList) {
      onRefreshTemplateList();
    }
  };

  const handleDeleteReport = async (report) => {
    setReportToDelete({
      ...report,
      file_name: report.s3_path ? report.s3_path.split('/').pop() : `Report V${report.ver}`
    });
    setDeleteMode('single');
    setShowDeleteConfirm(true);
  };

  const reportsInFlightRef = useRef(false);
  const reportControllerRef = useRef(null);
  useEffect(() => () => reportControllerRef.current?.abort(), []);
  const reportQueryRef = useRef('');
  reportQueryRef.current = JSON.stringify([templateId, size, page, searchTerm]);
  useEffect(() => registerRuntimeRefresh?.(signal => fetchReports({ background: true, signal })));

  const fetchReports = async ({ background = false, signal } = {}) => {
    if (background && reportsInFlightRef.current) return false;
    reportControllerRef.current?.abort();
    const controller = new AbortController();
    reportControllerRef.current = controller;
    const abort = () => controller.abort();
    signal?.addEventListener('abort', abort, { once: true });
    if (signal?.aborted) controller.abort();
    reportsInFlightRef.current = true;
    const query = reportQueryRef.current;
    try {
      if (!background) setLoading(true);
      if (!background && onProcessingStart) {
        onProcessingStart('Loading reports...');
      }

      const response = await axios.post(`${API_BASE_URL}list-reports`, {
        template_id: templateId,
        size: size,
        page_no: page,
        search: searchTerm || null
      }, {
        signal: controller.signal,
        headers: {
          Authorization: GetCookie('access_token')
        }
      });

      if (controller.signal.aborted || query !== reportQueryRef.current) return false;
      if (response.data.status === 'success') {
        const { reports: fetchedReports, total_reports, gen_status, capabilities } = response.data.data;
        setReports(fetchedReports);
        setTotalReports(total_reports);
        setGenStatus(gen_status);
        setCapabilities(capabilities || {});
        return true;
      } else {
        if (background) throw new Error(response.data.message || 'Failed to fetch reports');
        if (onError) {
          onError(response.data.message || 'Failed to fetch reports');
        }
      }
    } catch (error) {
      if (controller.signal.aborted || axios.isCancel(error)) return false;
      if (background) throw error;
      console.error('Error fetching reports:', error);
      if (onError) {
        onError('Failed to fetch reports');
      }
    } finally {
      signal?.removeEventListener('abort', abort);
      if (reportControllerRef.current === controller) {
        reportsInFlightRef.current = false;
        if (!background && !controller.signal.aborted) setLoading(false);
      }
      if (!background && onProcessingEnd && !controller.signal.aborted) {
        onProcessingEnd();
      }
    }
  };

  const handleSearch = () => {
    setSearchTerm(searchInput.trim());
    setPage(1);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSelectReport = (reportVer) => {
    setSelectedReports(prev => 
      prev.includes(reportVer) 
        ? prev.filter(id => id !== reportVer)
        : [...prev, reportVer]
    );
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedReports(reports.filter(r => r.status !== 'RUNNING').map(report => report.ver));
    } else {
      setSelectedReports([]);
    }
  };

  const handleDeleteSelected = () => {
    if (selectedReports.length === 0) {
      if (onError) {
        onError('No reports selected for deletion');
      }
      return;
    }
    setDeleteMode('multiple');
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    setShowDeleteConfirm(false);
    
    if (deleteMode === 'single' && reportToDelete) {
      await performSingleDelete(reportToDelete);
    } else if (deleteMode === 'multiple') {
      await performMultipleDelete();
    }
    
    setDeleteMode('single');
    setReportToDelete(null);
  };

  const performSingleDelete = async (report) => {
    if (onProcessingStart) {
      onProcessingStart(`Deleting report ${report.file_name}`);
    }

    try {
      const response = await axios.post(`${API_BASE_URL}delete-report`, {
        template_id: templateId,
        ver: report.ver
      }, {
        headers: {
          Authorization: GetCookie('access_token')
        }
      });

      if (response.data.status === 'success') {
        await fetchReports();
      } else {
        if (onError) {
          onError(response.data.message || 'Failed to delete report');
        }
      }
    } catch (error) {
      console.error('Error deleting report:', error);
      if (onError) {
        onError('Failed to delete report');
      }
    } finally {
      handleProcessingEndWithRefresh();
    }
  };

  const performMultipleDelete = async () => {
    if (onProcessingStart) {
      onProcessingStart(`Deleting ${selectedReports.length} selected reports`);
    }

    try {
      const response = await axios.post(`${API_BASE_URL}delete-many-reports`, {
        template_id: templateId,
        vers: selectedReports
      }, {
        headers: {
          Authorization: GetCookie('access_token')
        }
      });

      if (response.data.status === 'success') {
        setSelectedReports([]);
        await fetchReports();
      } else {
        if (onError) {
          onError(response.data.message || 'Failed to delete selected reports');
        }
      }
    } catch (error) {
      console.error('Error deleting selected reports:', error);
      if (onError) {
        onError('Failed to delete selected reports');
      }
    } finally {
      handleProcessingEndWithRefresh();
    }
  };

  useEffect(() => {
    if (templateId) {
      fetchReports();
    }
  }, [templateId, searchTerm, page, size]);

  const { formatDateTime } = useTimezone();

  const handleDownloadReport = async (report) => {
    if (report.status === 'FAILED') {
      if (onError) {
        onError('This report generation failed. Please generate a new report.');
      }
      return;
    }

    if (!report.s3_path) {
      if (onError) {
        onError('No file path available for download');
      }
      return;
    }

    if (onProcessingStart) {
      onProcessingStart(`Downloading ${report.s3_path.split('/').pop()}`);
    }

    try {
      const response = await axios.post(`${API_BASE_URL}download-report`, {
        template_id: templateId,
        ver: report.ver
      }, {
        headers: {
          Authorization: GetCookie('access_token')
        },
        responseType: 'blob'
      });

      const filename = report.s3_path.split('/').pop() || 'report.docx';
      const properFilename = filename.includes('.') ? filename : `${filename}.${report.file_type || 'docx'}`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', properFilename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error downloading report:', error);
      if (onError) {
        onError('Failed to download report');
      }
    } finally {
      if (onProcessingEnd) {
        onProcessingEnd();
      }
    }
  };

  const handlePreviewReport = (report) => {
    const displayName = report.s3_path ? report.s3_path.split('/').pop() : 'Report';
    setPreviewS3Path(report.s3_path);
    setPreviewDocumentName(displayName);
    setPreviewOpen(true);
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewS3Path(null);
    setPreviewDocumentName(null);
  };
 



  const getFileTypeColor = (fileType) => {
    switch (fileType?.toLowerCase()) {
      case 'pdf':
        return 'text-danger';
      case 'docx':
      case 'doc':
        return 'text-primary';
      case 'ppt':
      case 'pptx':
        return 'text-warning';
      default:
        return 'text-secondary';
    }
  };

  const getDeleteModalBodyContent = () => {
    if (deleteMode === 'single' && reportToDelete) {
      return (
        <div>
          <p className="mb-2">
            <strong>File:</strong> {reportToDelete.file_name}
          </p>
          <p className="mb-2">
            <strong>Version:</strong> V{reportToDelete.ver}
          </p>
          <p className="text-danger mb-0">This action cannot be undone.</p>
        </div>
      );
    } else if (deleteMode === 'multiple') {
      return (
        <div>
          <p className="mb-2">
            You are about to delete <strong>{selectedReports.length}</strong> selected reports.
          </p>
          <p className="text-danger mb-0">This action cannot be undone.</p>
        </div>
      );
    }
    return null;
  };

  const selectableReports = reports.filter(r => r.status !== 'RUNNING' && r.status !== 'FAILED');
  const isAllSelected = selectableReports.length > 0 && selectedReports.length === selectableReports.length;
  const isSomeSelected = selectedReports.length > 0 && !isAllSelected;

  return (
    <div className="ev-padding-small">
      <div>
        <h5 className="ev-med-p-blue">
          Generated Reports&nbsp;
          <span className="ev-reg">
            ({totalReports})
          </span>
        </h5>
      </div>

      <div className="row mt-3 mb-0">
        <div className="col-5 position-relative">
          <input
            type="text"
            className="form-control search-input ms-0"
            placeholder="Search reports"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={handleKeyPress}
          />
          <button 
            className="ev-btn-lightblue-fill-round search-bar-button"
            onClick={handleSearch}
            disabled={loading}
          >
            <i className="bi bi-search"></i>
          </button>
        </div>
        
        <div className="col-7 text-end">
          {genStatus && <StatusBadge status={genStatus} fullWidth={false} />}
          
          {canHere(PERMISSIONS.REPORT_DELETE_MANY) && (
            <button
              className="ev-btn-red-fill-round avatar-tooltip ms-2"
              onClick={handleDeleteSelected}
              data-tooltip={selectedReports.length > 0 ? 'Delete Selected' : 'Select reports to delete'}
              disabled={loading || selectedReports.length === 0}
            >
              <i className="bi bi-trash"></i> Delete Selected ({selectedReports.length})
            </button>
          )}
          
          <button 
            className="ev-btn-lightblue-fill-round ev-bold-reg ms-2" 
            onClick={fetchReports}
            disabled={loading}
          >
            <i className="bi bi-arrow-repeat"></i> Refresh 
          </button>
        </div>
      </div>

      {loading && (
      <div className="mb-5">
        <div className="ev-card-3 d-flex justify-content-center align-items-center  dashboard-right-contentarea-table-height-1 pt-5">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
      )}

      {!loading && (
        <div className="table-container">
          <table className="table-custom1 mt-0 overflow-hidden w-100">
            <thead>
              <tr>
                {canHere(PERMISSIONS.REPORT_DELETE_MANY) && (
                  <th className="w-5">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={isAllSelected}
                      ref={checkbox => {
                        if (checkbox) checkbox.indeterminate = isSomeSelected;
                      }}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </th>
                )}
                <th className="w-20">
                  <label className="ev-small-p-blue">File Name</label>
                </th>
                <th className="w-10">
                  <label className="ev-small-p-blue">Version</label>
                </th>
                <th className="w-10">
                  <label className="ev-small-p-blue">Language</label>
                </th>
                <th className="w-20">
                  <label className="ev-small-p-blue">Generated By</label>
                </th>
                <th className="w-15">
                  <label className="ev-small-p-blue">Generated At</label>
                </th>
                <th className="w-20">
                  <label className="ev-small-p-blue">Actions</label>
                </th>
              </tr>
            </thead>
          </table>
          
          <div className="scrollable-element dashboard-right-contentarea-table-height-1">
            <table className="table-custom1 overflow-hidden w-100">
              <tbody>
                {reports.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center">
                      <p className="ev-small-p-blue mt-5">No reports found</p>
                    </td>
                  </tr>
                ) : (
                  reports.map((report) => {
                    const isRunning = report.status === 'RUNNING';
                    const isFailed = report.status === 'FAILED';
                    const displayName = isRunning ? 'Unnamed Report' : (report.s3_path ? report.s3_path.split('/').pop() : 'Unnamed Report');

                    if (isRunning) {
                      return (
                        <tr key={report.report_id || report.ver} className="report-row-generating">
                          <td colSpan={7}>
                            <div className="report-gen-row">
                              <div className="report-gen-file">
                                <i className="bi bi-file-earmark-text report-gen-file-icon"></i>
                                <span className="report-gen-filename">Unnamed Report</span>
                              </div>
                              <div className="generating-loader-wrapper report-gen-loader">
                                {'Generating report...'.split('').map((ch, i) => (
                                  <span key={i} className="generating-loader-letter" style={{ animationDelay: `${0.1 + i * 0.095}s`, ...(ch === ' ' ? { width: '4px' } : {}) }}>{ch}</span>
                                ))}
                              </div>
                              <span className="report-gen-time">{formatDateTime(report.created_at)}</span>
                            </div>
                            <div className="genbi-refresh-hint report-gen-hint">
                              <span>Click</span>
                              <span className="genbi-refresh-hint-pill">
                                <i className="bi bi-arrow-repeat"></i> Refresh
                              </span>
                              <span>after a while to see the latest status</span>
                            </div>
                          </td>
                        </tr>
                      );
                    }

                    return (
                    <tr key={report.report_id || report.ver} className={isFailed ? 'report-row-failed' : ''}>
                      {canHere(PERMISSIONS.REPORT_DELETE_MANY) && (
                        <td className="align-top w-5">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={selectedReports.includes(report.ver)}
                            onChange={(e) => handleSelectReport(report.ver)}
                            disabled={isRunning}
                          />
                        </td>
                      )}
                      <td className="align-top w-20">
                        <span className="avatar-tooltip-bottom" data-tooltip={displayName}>
                          <TrimContent
                            text={displayName}
                            maxLength={20}
                          />
                        </span>
                        {isFailed && <><br /><StatusBadgeXSm status="FAILED" /></>}
                        {isFailed ? null : (
                        <button
                          className="ev-btn-blue-fill-round ev-btn-xsmall me-1"
                          onClick={() => handlePreviewReport(report)}
                          disabled={loading}
                        >
                          <i className="bi bi-eye"></i> View
                        </button>
                        )}
                      </td>
                      <td className="align-top w-10">
                        <div className="d-flex align-items-center ev-message-card2 ev-padding-xsmall  ev-fs-10 w-100 ev-radius-large ">
                          <span className={`ev-message-card5 ev-btn-xsmall w-100 text-center ${getFileTypeColor(report.file_type)}`}>
                          {report.file_type?.toUpperCase() || 'N/A'}
                        </span>
                          <span className="ms-1 me-1 ev-bold-reg">
                          V{report.ver || 'N/A'}
                        </span>
                        </div>

                      </td>
                      <td className="align-top w-10">
                        <span className="ev-reg-p-blue ev-fs-12">{report.language || 'N/A'}</span>
                      </td>
                      <td className="align-top w-20">
                        <div className="ev-message-card5 ev-fs-11 avatar-tooltip-bottom" data-tooltip={report.created_by || 'Unknown User'}>
                          <TrimContent
                            text={report.created_by || 'Unknown User'}
                            maxLength={15}
                          />
                        </div>
                      </td>
                      <td className="align-top w-15">
                        {formatDateTime(report.created_at)}
                      </td>
                      <td className="w-20">
                        <button
                          className="ev-btn-blue-outline-round ev-btn-small ev-bold-reg me-2"
                          onClick={() => handleDownloadReport(report)}
                          disabled={loading || isFailed}
                        >
                          Download <i className="bi bi-download"></i>
                        </button>
                        {canHere(PERMISSIONS.REPORT_DELETE) && (
                          <button
                            className="ev-btn-red-fill-round ev-btn-small ev-radius-xsmall pe-2 ps-2 avatar-tooltip"
                            onClick={() => handleDeleteReport(report)}
                            data-tooltip="Delete Report"
                            disabled={loading}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="ev-small-p-dark">
          Showing {reports.length} of {totalReports} entries
        </div>
        <div className="d-flex gap-2">
          <button 
            className="ev-btn-light-fill" 
            disabled={page === 1 || loading}
            onClick={() => setPage(page - 1)}
          >
            <i className="bi bi-chevron-left"></i> 
          </button>
          <button className="ev-btn-light-fill" disabled>
            Page {page} of {Math.max(1, Math.ceil(totalReports / size))}
          </button>
          <button 
            className="ev-btn-light-fill" 
            disabled={page >= Math.ceil(totalReports / size) || totalReports === 0 || loading}
            onClick={() => setPage(page + 1)}
          >
            <i className="bi bi-chevron-right"></i>
          </button>
        </div>
        <div>
          <PaginationControls 
            initialSize={size}
            onSizeChange={(newSize) => {
              setSize(newSize);
              setPage(1);
            }}
          />
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        show={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setReportToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        objectName={deleteMode === 'single' ? 'report' : 'reports'}
        itemName={
          deleteMode === 'single' && reportToDelete 
            ? reportToDelete.file_name 
            : `${selectedReports.length} selected reports`
        }
        bodyContent={getDeleteModalBodyContent()}
      />

      {/* Document Preview Component */}
      <DocumentPreview 
        isOpen={previewOpen}
        onClose={closePreview}
        s3_path={previewS3Path}
        bucket_type="output"
        documentName={previewDocumentName}
      />
    </div>
  );
};

export default GenbiReportView;
